import { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  Background, Controls, MiniMap, addEdge, applyEdgeChanges,
  applyNodeChanges, useReactFlow, ReactFlowProvider, Handle, Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { Plus, Play, Save, Trash2, Zap, Bot, PhoneMissed, FileText, UserPlus, Tag as TagIcon, AlertTriangle, Clock, Mail, MessageSquare, MessageCircle, CheckSquare, UserCheck, Slack, ChevronRight, X, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  wfCatalog, wfWorkflows, wfCreate, wfGet, wfPatch, wfDelete, wfTestRun,
} from "@/lib/api";

const ICONS = { UserPlus, FileText, PhoneMissed, AlertTriangle, Bot, TagIcon, Play, Mail, MessageSquare, MessageCircle, CheckSquare, Clock, UserCheck, Zap, Slack };

/* ─────────────────────────────── Custom node types */
function TriggerNode({ data, selected }) {
  return (
    <div className={`min-w-[180px] rounded-xl border-2 ${selected ? "border-indigo-500" : "border-indigo-300"} bg-indigo-50 shadow-sm`}>
      <div className="px-3 py-2 bg-indigo-600 text-white text-[10px] uppercase tracking-widest font-bold rounded-t-lg">Trigger</div>
      <div className="p-3">
        <div className="text-sm font-semibold text-slate-900">{data.label}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{data.kind}</div>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}
function ConditionNode({ data, selected }) {
  return (
    <div className={`min-w-[200px] rounded-xl border-2 ${selected ? "border-amber-500" : "border-amber-300"} bg-amber-50 shadow-sm`}>
      <Handle type="target" position={Position.Top} />
      <div className="px-3 py-2 bg-amber-500 text-white text-[10px] uppercase tracking-widest font-bold rounded-t-lg">If</div>
      <div className="p-3">
        <div className="text-sm font-semibold text-slate-900">{data.label}</div>
        <div className="text-[10px] text-slate-600 mt-0.5">{data.params?.field} {data.params?.op} {String(data.params?.value ?? "")}</div>
      </div>
      <Handle id="true" type="source" position={Position.Bottom} style={{ left: "25%" }} />
      <Handle id="false" type="source" position={Position.Bottom} style={{ left: "75%" }} />
      <div className="flex justify-between px-3 pb-2 text-[10px] font-semibold">
        <span className="text-emerald-700">yes</span>
        <span className="text-red-700">no</span>
      </div>
    </div>
  );
}
function ActionNode({ data, selected }) {
  return (
    <div className={`min-w-[180px] rounded-xl border-2 ${selected ? "border-cyan-500" : "border-cyan-300"} bg-cyan-50 shadow-sm`}>
      <Handle type="target" position={Position.Top} />
      <div className="px-3 py-2 bg-cyan-600 text-white text-[10px] uppercase tracking-widest font-bold rounded-t-lg">Action</div>
      <div className="p-3">
        <div className="text-sm font-semibold text-slate-900">{data.label}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{data.kind}</div>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}
const nodeTypes = { trigger: TriggerNode, condition: ConditionNode, action: ActionNode };

/* ────────────────────────────────────────── Main page */
export default function WorkflowBuilder() {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner />
    </ReactFlowProvider>
  );
}

function WorkflowBuilderInner() {
  const [list, setList] = useState([]);
  const [wid, setWid] = useState(null);
  const [wf, setWf] = useState(null);
  const [runs, setRuns] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const rf = useReactFlow();

  useEffect(() => { document.title = "Workflow Builder | WebsiteTech.pro"; }, []);
  useEffect(() => { wfCatalog().then(setCatalog).catch(() => {}); wfWorkflows().then((d) => setList(d.items || [])).catch(() => {}); }, []);

  const load = useCallback(async (id) => {
    setWid(id);
    const d = await wfGet(id);
    setWf(d.workflow); setRuns(d.runs || []);
    setNodes(d.workflow.nodes || []); setEdges(d.workflow.edges || []);
    setDirty(false); setLastRun(null);
  }, []);

  const onNodesChange = useCallback((ch) => { setNodes((n) => applyNodeChanges(ch, n)); setDirty(true); }, []);
  const onEdgesChange = useCallback((ch) => { setEdges((e) => applyEdgeChanges(ch, e)); setDirty(true); }, []);
  const onConnect = useCallback((c) => { setEdges((e) => addEdge({ ...c, animated: true }, e)); setDirty(true); }, []);
  const onNodeClick = useCallback((_e, n) => setSelected(n), []);

  const createWf = async () => {
    const name = prompt("Name your workflow:");
    if (!name) return;
    const w = await wfCreate({ name });
    setList((l) => [w, ...l]);
    load(w.id);
  };
  const deleteWf = async () => {
    if (!wid) return;
    if (!confirm("Delete this workflow?")) return;
    await wfDelete(wid); setWid(null); setWf(null); setNodes([]); setEdges([]);
    wfWorkflows().then((d) => setList(d.items || []));
  };
  const save = async () => {
    if (!wid) return;
    setSaving(true);
    try {
      const updated = await wfPatch(wid, { nodes, edges });
      setWf(updated); setDirty(false);
    } finally { setSaving(false); }
  };
  const runTest = async () => {
    if (!wid) return;
    if (dirty) await save();
    setRunning(true);
    try {
      const run = await wfTestRun(wid, { name: "Alex", email: "alex@example.com", score: 8 });
      setLastRun(run);
      setRuns((r) => [run, ...r]);
    } finally { setRunning(false); }
  };
  const toggleActive = async () => {
    if (!wf) return;
    const nextStatus = wf.status === "active" ? "paused" : "active";
    const up = await wfPatch(wid, { status: nextStatus });
    setWf(up);
    setList((l) => l.map((x) => (x.id === wid ? up : x)));
  };

  const addNode = (spec) => {
    const id = `${spec.type}-${Date.now().toString(36)}`;
    const y = 60 + nodes.length * 90;
    const label = spec.label;
    const data =
      spec.type === "trigger"    ? { label, kind: spec.kind, params: {} } :
      spec.type === "action"     ? { label, kind: spec.kind, params: defaultActionParams(spec.kind) } :
      /* condition */              { label, kind: "condition", params: { field: "score", op: "gt", value: 7 } };
    const node = { id, type: spec.type, position: { x: 340, y }, data };
    setNodes((n) => [...n, node]);
    setDirty(true);
  };
  function defaultActionParams(kind) {
    if (kind === "send_email") return { to: "{{email}}", subject: "Hi {{name}}", html: "<p>Hello {{name}}!</p>" };
    if (kind === "send_sms" || kind === "send_whatsapp") return { to: "{{phone}}", body: "Hi {{name}} — thanks for reaching out." };
    if (kind === "wait") return { seconds: 60 };
    if (kind === "create_task") return { title: "Follow up with {{name}}", assignee: "owner" };
    if (kind === "add_tag") return { tag: "workflow-touched" };
    if (kind === "assign_owner") return { owner_id: "" };
    if (kind === "webhook") return { url: "https://example.com/hook" };
    if (kind === "notify_slack") return { channel: "leads" };
    return {};
  }

  const updateSelected = (patch) => {
    if (!selected) return;
    setNodes((n) => n.map((nd) => nd.id === selected.id ? { ...nd, data: { ...nd.data, ...patch, params: { ...(nd.data.params || {}), ...(patch.params || {}) } } } : nd));
    setSelected((s) => ({ ...s, data: { ...s.data, ...patch, params: { ...(s.data.params || {}), ...(patch.params || {}) } } }));
    setDirty(true);
  };
  const deleteSelected = () => {
    if (!selected) return;
    setNodes((n) => n.filter((nd) => nd.id !== selected.id));
    setEdges((e) => e.filter((ed) => ed.source !== selected.id && ed.target !== selected.id));
    setSelected(null); setDirty(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 w-full" data-testid="workflow-builder">
        <header className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Sparkles className="w-3.5 h-3.5" /> Workflow builder
          </div>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900">Drag together your automation</h1>
          <p className="mt-1 text-sm text-slate-600">Chain triggers → conditions → actions across email, SMS, WhatsApp, tasks and webhooks. Test-run in one click.</p>
        </header>

        <div className="flex gap-3 px-4 sm:px-6 lg:px-8 pb-6 h-[calc(100vh-220px)] min-h-[560px]">
          {/* Left: workflow list */}
          <aside className="w-60 flex-shrink-0 bg-white border border-slate-200 rounded-2xl p-3 overflow-y-auto">
            <button data-testid="wf-new" onClick={createWf} className="w-full inline-flex items-center justify-center gap-1 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg px-3 py-2 mb-3"><Plus size={14} /> New workflow</button>
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 px-1">Your workflows</div>
            <div className="space-y-1">
              {list.length === 0 && <div className="text-xs text-slate-500 px-1 py-3">No workflows yet.</div>}
              {list.map((w) => (
                <button key={w.id} data-testid={`wf-item-${w.id.slice(0, 6)}`} onClick={() => load(w.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${wid === w.id ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}>
                  <div className="font-semibold truncate">{w.name}</div>
                  <div className={`text-[10px] uppercase tracking-widest ${wid === w.id ? "text-slate-300" : "text-slate-500"}`}>{w.status} · {w.runs_count || 0} runs</div>
                </button>
              ))}
            </div>
          </aside>

          {/* Center: canvas */}
          <section className="flex-1 bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
              {wf ? (
                <>
                  <div className="font-semibold text-slate-900">{wf.name}</div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 ${wf.status === "active" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{wf.status}</span>
                  {dirty && <span className="text-[11px] text-amber-600">unsaved</span>}
                  <div className="ml-auto flex gap-2">
                    <button data-testid="wf-save" onClick={save} disabled={!dirty || saving} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-slate-900 text-white font-semibold hover:bg-slate-700 disabled:opacity-50"><Save size={12} /> {saving ? "Saving…" : "Save"}</button>
                    <button data-testid="wf-run" onClick={runTest} disabled={running} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-500 disabled:opacity-50"><Play size={12} /> {running ? "Running…" : "Test run"}</button>
                    <button data-testid="wf-activate" onClick={toggleActive} className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md font-semibold ${wf.status === "active" ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"}`}>{wf.status === "active" ? "Pause" : "Activate"}</button>
                    <button onClick={deleteWf} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md text-slate-400 hover:text-red-600"><Trash2 size={12} /></button>
                  </div>
                </>
              ) : <div className="text-sm text-slate-500">Select or create a workflow to start building.</div>}
            </div>

            <div className="flex-1 relative">
              {wf ? (
                <ReactFlow
                  nodes={nodes} edges={edges}
                  onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                  onConnect={onConnect} onNodeClick={onNodeClick}
                  nodeTypes={nodeTypes} fitView proOptions={{ hideAttribution: true }}
                >
                  <Background gap={16} />
                  <Controls />
                  <MiniMap zoomable pannable />
                </ReactFlow>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">Pick a workflow on the left.</div>
              )}
            </div>
          </section>

          {/* Right: palette + inspector + last run */}
          <aside className="w-72 flex-shrink-0 space-y-3 overflow-y-auto">
            {catalog && wf && (
              <div className="bg-white border border-slate-200 rounded-2xl p-3">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 px-1">Add node</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {catalog.triggers.map((t) => <PaletteBtn key={t.kind} testid={`wf-add-trigger-${t.kind}`} onClick={() => addNode({ type: "trigger", ...t })} icon={ICONS[t.icon] || Zap} label={t.label} color="indigo" />)}
                  <PaletteBtn testid="wf-add-condition" onClick={() => addNode({ type: "condition", label: "Condition" })} icon={ChevronRight} label="If / else" color="amber" />
                  {catalog.actions.map((a) => <PaletteBtn key={a.kind} testid={`wf-add-action-${a.kind}`} onClick={() => addNode({ type: "action", ...a })} icon={ICONS[a.icon] || Zap} label={a.label} color="cyan" />)}
                </div>
              </div>
            )}

            {selected && (
              <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="wf-inspector">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Inspector</div>
                  <button onClick={deleteSelected} className="text-slate-400 hover:text-red-600 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> delete</button>
                </div>
                <div className="text-xs text-slate-500 mb-1">{selected.type} · {selected.data.kind}</div>
                <input value={selected.data.label} onChange={(e) => updateSelected({ label: e.target.value })} className="w-full text-sm font-semibold border-b border-slate-200 py-1 mb-2 focus:outline-none focus:border-indigo-500" />
                {selected.type === "condition" && (
                  <div className="space-y-2 text-xs">
                    <input placeholder="field" value={selected.data.params?.field || ""} onChange={(e) => updateSelected({ params: { field: e.target.value } })} className="w-full border border-slate-300 rounded px-2 py-1" />
                    <select value={selected.data.params?.op || "equals"} onChange={(e) => updateSelected({ params: { op: e.target.value } })} className="w-full border border-slate-300 rounded px-2 py-1">
                      {(catalog?.conditions || []).map((c) => <option key={c.op} value={c.op}>{c.label}</option>)}
                    </select>
                    <input placeholder="value" value={selected.data.params?.value ?? ""} onChange={(e) => updateSelected({ params: { value: e.target.value } })} className="w-full border border-slate-300 rounded px-2 py-1" />
                  </div>
                )}
                {selected.type === "action" && (
                  <ParamsEditor kind={selected.data.kind} params={selected.data.params || {}} onChange={(params) => updateSelected({ params })} />
                )}
              </div>
            )}

            {lastRun && (
              <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="wf-last-run">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Last run</div>
                <div className="text-xs text-slate-600 mb-2">{new Date(lastRun.started_at).toLocaleString()}</div>
                <div className="space-y-1.5">
                  {lastRun.steps.map((s, i) => (
                    <div key={i} className="text-xs flex items-start gap-2">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[9px] font-bold flex-shrink-0 ${s.status === "sent" || s.status === "fired" || s.status === "queued" || s.status === "scheduled" ? "bg-emerald-500" : s.status === "mocked" ? "bg-amber-500" : s.status === "evaluated" ? "bg-blue-500" : "bg-slate-400"}`}>{i + 1}</span>
                      <div>
                        <div className="font-semibold text-slate-800">{s.label}</div>
                        <div className="text-slate-500">{s.status} · {s.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PaletteBtn({ icon: Icon, label, onClick, color = "indigo", testid }) {
  const colors = { indigo: "hover:bg-indigo-50 hover:border-indigo-300", cyan: "hover:bg-cyan-50 hover:border-cyan-300", amber: "hover:bg-amber-50 hover:border-amber-300" };
  return (
    <button data-testid={testid} onClick={onClick} className={`text-left border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-semibold text-slate-700 flex items-center gap-1.5 bg-white ${colors[color]}`}>
      <Icon className="w-3.5 h-3.5" /> <span className="truncate">{label}</span>
    </button>
  );
}

function ParamsEditor({ kind, params, onChange }) {
  const upd = (patch) => onChange({ ...params, ...patch });
  if (kind === "send_email") return (
    <div className="space-y-2 text-xs">
      <input placeholder="to (supports {{email}})" value={params.to || ""} onChange={(e) => upd({ to: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1" />
      <input placeholder="subject" value={params.subject || ""} onChange={(e) => upd({ subject: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1" />
      <textarea placeholder="HTML body" value={params.html || ""} onChange={(e) => upd({ html: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 min-h-[60px]" />
    </div>
  );
  if (kind === "send_sms" || kind === "send_whatsapp") return (
    <div className="space-y-2 text-xs">
      <input placeholder="to (supports {{phone}})" value={params.to || ""} onChange={(e) => upd({ to: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1" />
      <textarea placeholder="body" value={params.body || ""} onChange={(e) => upd({ body: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 min-h-[60px]" />
      <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">MOCKED — connect Sent.dm / Twilio to send live.</div>
    </div>
  );
  if (kind === "wait") return (
    <div className="space-y-2 text-xs">
      <input type="number" placeholder="seconds" value={params.seconds || 60} onChange={(e) => upd({ seconds: Number(e.target.value) })} className="w-full border border-slate-300 rounded px-2 py-1" />
    </div>
  );
  if (kind === "create_task") return (
    <div className="space-y-2 text-xs">
      <input placeholder="title" value={params.title || ""} onChange={(e) => upd({ title: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1" />
      <input placeholder="assignee" value={params.assignee || ""} onChange={(e) => upd({ assignee: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1" />
    </div>
  );
  if (kind === "add_tag") return <input placeholder="tag" value={params.tag || ""} onChange={(e) => upd({ tag: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />;
  if (kind === "webhook") return <input placeholder="URL" value={params.url || ""} onChange={(e) => upd({ url: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />;
  if (kind === "notify_slack") return <input placeholder="channel" value={params.channel || ""} onChange={(e) => upd({ channel: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />;
  return <div className="text-xs text-slate-500">No editable params.</div>;
}
